const DASHBOARD_PERMISSIONS = {
  orders: "purchasing.orders.view_tab",
  requisitions: "purchasing.requisitions.view_tab",
  receivings: "purchasing.receivings.view_tab",
  deliveryNotes: "purchasing.delivery_notes.view_tab",
  stock: "purchasing.stock.current",
  report: "purchasing.reports.items_purchased.view",
};

const STATUS_LABELS = {
  en: {
    draft: "Draft",
    pending_approval: "Pending approval",
    sent: "Sent",
    acknowledged: "Acknowledged",
    confirmed: "Confirmed",
    partial: "Partially received",
    completed: "Completed",
    cancelled: "Cancelled",
    rejected: "Rejected",
  },
  es: {
    draft: "Borrador",
    pending_approval: "Pendiente de aprobación",
    sent: "Enviado",
    acknowledged: "Acuse de recibo",
    confirmed: "Confirmado",
    partial: "Recepción parcial",
    completed: "Completado",
    cancelled: "Cancelado",
    rejected: "Rechazado",
  },
};

const isSpanish = (languageId) => /^es(?:-|$)/i.test(String(languageId || ""));

function finiteNumber(value) {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function monthIndex(periodKey) {
  if (typeof periodKey !== "string" || !/^\d{4}-(0[1-9]|1[0-2])$/.test(periodKey)) {
    return null;
  }
  return Number(periodKey.slice(0, 4)) * 12 + Number(periodKey.slice(5, 7)) - 1;
}

function dateMonthIndex(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) return null;
  return monthIndex(value.slice(0, 7));
}

function keyForMonth(index) {
  return `${String(Math.floor(index / 12)).padStart(4, "0")}-${String((index % 12) + 1).padStart(2, "0")}`;
}

/** Fill only the API's reporting window, while retaining every returned month. */
export function buildSpendTrend(series, window, locale = "en-US") {
  const rows = new Map();
  for (const row of Array.isArray(series) ? series : []) {
    const index = monthIndex(row?.periodKey);
    if (index === null) continue;
    rows.set(index, {
      ...row,
      spend: finiteNumber(row.spend) ?? 0,
      quantity: finiteNumber(row.quantity) ?? 0,
      receipts: finiteNumber(row.receipts) ?? 0,
    });
  }

  // No receipts is a real empty state, not an inferred sequence of zeroes.
  if (rows.size === 0) return [];

  const start = dateMonthIndex(window?.lookbackStart);
  const end = dateMonthIndex(window?.today);
  if (start !== null && end !== null && start <= end) {
    for (let index = start; index <= end; index += 1) {
      if (!rows.has(index)) {
        rows.set(index, { periodKey: keyForMonth(index), spend: 0, quantity: 0, receipts: 0 });
      }
    }
  }

  const formatter = new Intl.DateTimeFormat(locale, {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  });

  return Array.from(rows.entries())
    .sort(([left], [right]) => left - right)
    .map(([, row]) => ({
      ...row,
      periodLabel: formatter.format(new Date(`${row.periodKey}-01T00:00:00.000Z`)),
    }));
}

export function getDashboardAccess(user) {
  const isAdmin = Number(user?.id) === 1;
  return Object.fromEntries(
    Object.entries(DASHBOARD_PERMISSIONS).map(([key, permission]) => [
      key,
      isAdmin || Boolean(user?.permissions?.[permission]),
    ])
  );
}

export function dashboardPath(path, businessUnitId) {
  if (businessUnitId === null || businessUnitId === undefined || String(businessUnitId).trim() === "") {
    return path;
  }
  const hashIndex = path.indexOf("#");
  const pathname = hashIndex === -1 ? path : path.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : path.slice(hashIndex);
  const separator = pathname.includes("?") ? (/[?&]$/.test(pathname) ? "" : "&") : "?";
  return `${pathname}${separator}business_unit_id=${encodeURIComponent(String(businessUnitId))}${hash}`;
}

export function formatDashboardNumber(value, locale, options = {}) {
  const numeric = finiteNumber(value);
  return numeric === null ? "—" : new Intl.NumberFormat(locale, options).format(numeric);
}

export function getStatusLabel(status, languageId) {
  const language = isSpanish(languageId) ? "es" : "en";
  return Object.prototype.hasOwnProperty.call(STATUS_LABELS[language], status)
    ? STATUS_LABELS[language][status]
    : language === "es" ? "Desconocido" : "Unknown";
}

export function getBucketLabel(bucket, languageId) {
  const days = isSpanish(languageId) ? "días" : "days";
  const range = /^(\d+)-(\d+)d$/.exec(String(bucket || ""));
  if (range) return `${range[1]}–${range[2]} ${days}`;
  const older = /^(\d+)\+d$/.exec(String(bucket || ""));
  if (older) return `${isSpanish(languageId) ? "Más de" : "Over"} ${older[1]} ${days}`;
  return bucket || "—";
}

export function getCategoryLabel(value, languageId) {
  return value === "Uncategorised"
    ? isSpanish(languageId) ? "Sin categoría" : "Uncategorised"
    : value;
}
